import { useMemo, useState, type ReactNode } from 'react'
import EmptyState from '../../components/shell/EmptyState'
import { COVERAGE_REPORT_STATUS_LABEL, filterReportRows, toReportRows, type CoverageReportStatus } from './coverageReport'
import { coverageFilters, type CoverageFilterState } from './coverageFilters'
import { belowAvailableThreshold, AVAILABLE_THRESHOLD_PERCENT, manpowerByDay, manpowerByShift, manpowerTotals, type ManpowerRow } from './manpowerFigures'
import { manpowerExportTable } from './manpowerReportExport'
import ReportControls, { RangeFields } from './ReportControls'
import { exportExcel, exportPdf } from './reportExport'
import { useCoverageData } from './useCoverageData'
import WorkbookExport from './WorkbookExport'
import { useReportRange } from './useReportRange'

/**
 * docs/product/reports.md's two-week manpower report: each day of the pay
 * period with its manpower, availability, leave, detail and planned overtime,
 * and the same figures by shift.
 */
const STATUS_CLASS: Record<CoverageReportStatus, string> = {
  understaffed: 'text-error',
  'missing-qualification': 'text-error',
  'at-risk': 'text-warning',
  'adequately-staffed': 'text-success',
  overstaffed: 'text-info',
  'no-target': 'text-ink-muted',
}

const HEADER_CLASS = 'px-4 py-2.5 text-caption font-bold tracking-wide text-ink-subtle uppercase'
const CELL_CLASS = 'px-4 py-2.5 text-small [font-variant-numeric:tabular-nums]'

function dateLabel(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

const hours = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(1))
const percent = (value: number | null) => (value === null ? 'None' : `${value}%`)

function Tile({ label, value, note, warn }: { label: string; value: number | string; note?: string; warn?: boolean }) {
  return (
    <div className="rounded-card border border-line bg-canvas p-4">
      <p className="text-caption font-bold tracking-wide text-ink-subtle uppercase">{label}</p>
      <p className={`mt-1 text-h2 font-extrabold [font-variant-numeric:tabular-nums] ${warn ? 'text-error' : ''}`}>{value}</p>
      {warn && <span className="sr-only">Below {AVAILABLE_THRESHOLD_PERCENT}%</span>}
      {note && <p className="mt-0.5 text-caption text-ink-muted">{note}</p>}
    </div>
  )
}

/** The two-week report: the pay period's days, chosen by pay period or dates. */
export default function ManpowerReport() {
  const range = useReportRange()
  return (
    <ManpowerView
      range={range}
      rangeFields={<RangeFields range={range} />}
      regionLabel="Two-week manpower"
      title="Two-Week Manpower Report"
      fileStem="two-week-manpower"
      emptyText="No manpower for these dates. Choose another pay period or clear a filter."
    />
  )
}

/**
 * The manpower figures for one date range, as the two-week and monthly
 * reports show them: the totals, the filters, the by-day and by-shift tables,
 * and the exports. A report supplies its own range and the fields that choose it.
 */
export function ManpowerView({
  range,
  rangeFields,
  regionLabel,
  title,
  fileStem,
  emptyText,
}: {
  range: { from: string; to: string; invalid: boolean }
  rangeFields: ReactNode
  regionLabel: string
  title: string
  fileStem: string
  emptyText: string
}) {
  const { from, to } = range
  const [filters, setFilters] = useState<CoverageFilterState>({})
  const [view, setView] = useState<'day' | 'shift'>('day')
  const { rows, lookups, loading, failed } = useCoverageData(from, to)
  const [exportNotice, setExportNotice] = useState<{ kind: 'done' | 'failed'; text: string } | null>(null)

  const reportRows = useMemo(() => toReportRows(rows, lookups), [rows, lookups])
  const filtered = useMemo(() => filterReportRows(reportRows, { from, to, ...filters }), [reportRows, from, to, filters])
  const tableRows = useMemo(() => (view === 'day' ? manpowerByDay(filtered) : manpowerByShift(filtered)), [filtered, view])
  const totals = useMemo(() => manpowerTotals(filtered), [filtered])
  const reportFilters = coverageFilters(reportRows, lookups, filters, setFilters, ['team', 'supervisor', 'shift', 'status'])

  async function runExport(format: 'pdf' | 'xlsx') {
    const table = manpowerExportTable(tableRows, totals, { from, to, filters, lookups, view, generatedAt: new Date(), title })
    const fileName = `muster-${fileStem}-${from}-to-${to}.${format}`
    try {
      if (format === 'pdf') await exportPdf(table, fileName)
      else await exportExcel(table, fileName)
      setExportNotice({ kind: 'done', text: `Exported ${fileName}` })
    } catch {
      setExportNotice({ kind: 'failed', text: 'The export could not be created. Try again.' })
    }
  }

  const canExport = !loading && !failed && !range.invalid && tableRows.length > 0

  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Totals" className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Tile label="Available" value={percent(totals.availablePercent)} warn={belowAvailableThreshold(totals.availablePercent)} note={totals.totalManpower === 0 ? undefined : `${totals.available} of ${totals.totalManpower}`} />
        <Tile label="Total manpower" value={totals.totalManpower} />
        <Tile label="On duty" value={totals.onDuty} />
        <Tile label="On leave" value={totals.onLeave} />
        <Tile label="Detailed out" value={totals.detailedOut} />
        <Tile label="Planned overtime" value={`${hours(totals.plannedOvertime)} h`} />
      </section>

      <ReportControls rangeFields={rangeFields} filters={reportFilters} onReset={() => setFilters({})} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="group" aria-label="Report view" className="flex">
          {(['day', 'shift'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={view === option}
              onClick={() => setView(option)}
              className={`min-h-11 border border-line-strong px-4 text-small font-bold first:rounded-l-control last:rounded-r-control ${
                view === option ? 'border-primary bg-selected' : 'hover:bg-surface-2'
              }`}
            >
              {option === 'day' ? 'By day' : 'By shift'}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <p role="status" className="text-small text-ink-muted">
            {exportNotice?.kind === 'done' ? exportNotice.text : loading ? 'Loading' : `${tableRows.length} rows`}
          </p>
          <button
            type="button"
            disabled={!canExport}
            onClick={() => runExport('pdf')}
            className="min-h-11 rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2 disabled:opacity-50"
          >
            Export PDF
          </button>
          <button
            type="button"
            disabled={!canExport}
            onClick={() => runExport('xlsx')}
            className="min-h-11 rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2 disabled:opacity-50"
          >
            Export Excel
          </button>
          <WorkbookExport />
        </div>
      </div>
      {exportNotice?.kind === 'failed' && (
        <p role="alert" className="text-small text-error">
          {exportNotice.text}
        </p>
      )}

      {failed ? (
        <p role="alert" className="rounded-card border border-line bg-canvas p-4 text-small text-error">
          The manpower report could not be loaded. Reload the page, or try again in a moment.
        </p>
      ) : (
        <div role="region" aria-label={regionLabel} tabIndex={0} className="overflow-x-auto rounded-card border border-line bg-canvas">
          {loading ? (
            <div aria-hidden="true" className="flex flex-col gap-2 p-4">
              {[0, 1, 2, 3].map((line) => (
                <div key={line} className="h-8 animate-pulse rounded-control bg-surface-2" />
              ))}
            </div>
          ) : (
            <ManpowerTable rows={tableRows} byShift={view === 'shift'} emptyText={emptyText} />
          )}
        </div>
      )}
    </div>
  )
}

function ManpowerTable({ rows, byShift, emptyText }: { rows: ManpowerRow[]; byShift: boolean; emptyText: string }) {
  if (rows.length === 0) {
    return <EmptyState>{emptyText}</EmptyState>
  }
  return (
    <table className="w-full min-w-[860px] border-collapse">
      <caption className="sr-only">{byShift ? 'Manpower by date and shift' : 'Manpower by date'}</caption>
      <thead>
        <tr className="border-b border-line bg-surface-2 text-left">
          <th scope="col" className={HEADER_CLASS}>Date</th>
          {byShift && <th scope="col" className={HEADER_CLASS}>Shift</th>}
          {['Total manpower', 'On duty', 'Available', 'Available %', 'On leave', 'Detailed out', 'Planned overtime (h)'].map((label) => (
            <th key={label} scope="col" className={`${HEADER_CLASS} text-right`}>{label}</th>
          ))}
          <th scope="col" className={HEADER_CLASS}>Coverage status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key} className="border-b border-line last:border-b-0">
            <th scope="row" className={`${CELL_CLASS} text-left font-semibold`}>{dateLabel(row.date)}</th>
            {byShift && <td className={CELL_CLASS}>{row.shift}</td>}
            <td className={`${CELL_CLASS} text-right`}>{row.totalManpower}</td>
            <td className={`${CELL_CLASS} text-right`}>{row.onDuty}</td>
            <td className={`${CELL_CLASS} text-right`}>{row.available}</td>
            <td className={`${CELL_CLASS} text-right ${belowAvailableThreshold(row.availablePercent) ? 'font-bold text-error' : ''}`}>
              {percent(row.availablePercent)}
              {belowAvailableThreshold(row.availablePercent) && <span className="sr-only"> (below {AVAILABLE_THRESHOLD_PERCENT}%)</span>}
            </td>
            <td className={`${CELL_CLASS} text-right`}>{row.onLeave}</td>
            <td className={`${CELL_CLASS} text-right`}>{row.detailedOut}</td>
            <td className={`${CELL_CLASS} text-right`}>{hours(row.plannedOvertime)}</td>
            <td className={`${CELL_CLASS} font-semibold ${STATUS_CLASS[row.status]}`}>{COVERAGE_REPORT_STATUS_LABEL[row.status]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
